import { useState } from 'react';
import { Link } from 'react-router-dom';

export default function HomePage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [isScrolled, setIsScrolled] = useState(false);

  useState(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  });

  const testimonials = [
    {
      name: 'Sarah Mitchell',
      location: 'Downtown District',
      rating: 5,
      text: 'Absolutely fantastic service! They arrived on time, worked efficiently, and cleared out my entire garage in under two hours. The team was professional, courteous, and left the space spotless. I couldn\'t be happier with the results and the pricing was very reasonable.',
      avatar: 'https://public.readdy.ai/ai/img_res/18e09176c1d38b8151add4b41a01114b.jpg'
    },
    {
      name: 'Michael Chen',
      location: 'Riverside Area',
      rating: 5,
      text: 'I needed same-day service for an estate cleanout and they delivered beyond expectations. The crew was respectful of the property, handled everything with care, and completed the job faster than I anticipated. Highly recommend for anyone needing reliable junk removal.',
      avatar: 'https://readdy.ai/api/search-image?query=professional%20asian%20man%20smiling%20headshot%20portrait%20clean%20white%20background%20natural%20lighting%20confident%20friendly%20expression%20business%20casual%20attire&width=80&height=80&seq=testimonial2&orientation=squarish'
    },
    {
      name: 'Jennifer Rodriguez',
      location: 'Oakwood Neighborhood',
      rating: 5,
      text: 'Best junk removal company I\'ve ever worked with! They removed old furniture, appliances, and years of accumulated items from my basement. The pricing was transparent with no hidden fees, and they even swept up afterwards. Will definitely use them again.',
      avatar: 'https://readdy.ai/api/search-image?query=professional%20hispanic%20woman%20smiling%20headshot%20portrait%20clean%20white%20background%20natural%20lighting%20confident%20friendly%20expression%20business%20casual%20attire&width=80&height=80&seq=testimonial3&orientation=squarish'
    }
  ];

  const faqs = [
    {
      question: 'What items do you accept for removal?',
      answer: 'We accept almost everything including furniture, appliances, electronics, mattresses, yard waste, construction debris, and general household junk. We cannot accept hazardous materials like chemicals, paint, asbestos, or medical waste. If you\'re unsure about an item, just give us a call and we\'ll let you know.'
    },
    {
      question: 'How quickly can you come to remove my junk?',
      answer: 'We offer same-day and next-day service in most areas. Our typical response time is within 24 hours of your booking. For urgent removals, we can often accommodate same-day requests if you call us in the morning. We work seven days a week to serve you better.'
    },
    {
      question: 'Do you offer same-day service?',
      answer: 'Yes! We understand that sometimes you need junk removed immediately. If you call us before noon, we can usually schedule a same-day pickup. Same-day service is subject to availability in your area, so we recommend calling as early as possible to secure your spot.'
    },
    {
      question: 'How is pricing calculated?',
      answer: 'Our pricing is based on the volume of junk you need removed, measured in truck loads. We offer transparent, upfront quotes with no hidden fees. Prices range from $150-$200 for a quarter truck load up to $500-$650 for a full truck load. We provide free estimates and you only pay for the space your items take up.'
    },
    {
      question: 'Do I need to be present during the removal?',
      answer: 'While we prefer you to be present to point out items and answer any questions, it\'s not always necessary. If you can\'t be there, just provide clear instructions about what needs to be removed and ensure we have access to the area. Many customers leave a key or garage code for our team.'
    },
    {
      question: 'Do you recycle or donate items?',
      answer: 'Absolutely! We\'re committed to eco-friendly practices. We sort through all items and donate usable furniture, appliances, and household goods to local charities. We also recycle metals, electronics, and other recyclable materials whenever possible, keeping items out of landfills.'
    }
  ];

  return (
    <div className="min-h-screen bg-white font-['Inter',sans-serif]">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center">
            <img src="https://static.readdy.ai/image/f76823ce8be4897f48af4b03ccc6bd18/ac857630e4931604e864e54d30baa87b.png" alt="Junk Removal Company" className="h-12 md:h-16 w-auto" />
          </div>
          <div className="hidden md:flex items-center gap-8">
            <a href="#services" className={`text-sm font-medium transition-colors ${isScrolled ? 'text-gray-700 hover:text-emerald-600' : 'text-white hover:text-emerald-300'}`}>Services</a>
            <a href="#testimonials" className={`text-sm font-medium transition-colors ${isScrolled ? 'text-gray-700 hover:text-emerald-600' : 'text-white hover:text-emerald-300'}`}>Testimonials</a>
            <a href="#faq" className={`text-sm font-medium transition-colors ${isScrolled ? 'text-gray-700 hover:text-emerald-600' : 'text-white hover:text-emerald-300'}`}>FAQ</a>
          </div>
          <div className="flex items-center gap-4">
            <a href="tel:+18633000390" className={`hidden md:block text-sm font-bold whitespace-nowrap ${isScrolled ? 'text-gray-900' : 'text-white'}`}>(863) 300-0390</a>
            <a href="tel:+18633000390" className="bg-emerald-500 hover:bg-emerald-600 text-white px-4 md:px-6 py-2 md:py-2.5 rounded-full text-xs md:text-sm font-semibold transition-colors whitespace-nowrap cursor-pointer">CALL NOW</a>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 w-full h-full">
          <img 
            src="https://readdy.ai/api/search-image?query=professional%20junk%20removal%20service%20large%20truck%20being%20loaded%20with%20furniture%20and%20household%20items%20by%20uniformed%20workers%20efficient%20team%20in%20action%20bright%20sunny%20day%20residential%20neighborhood%20driveway%20clean%20organized%20professional%20operation%20modern%20equipment%20high%20quality%20service%20industry%20photography&width=1920&height=1080&seq=hero2&orientation=landscape" 
            alt="Professional Junk Removal Service" 
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center w-full pt-20 md:pt-0">
          <h1 className="text-3xl md:text-7xl font-bold text-white mb-3 md:mb-6 leading-tight">
            FAST & AFFORDABLE<br />
            JUNK REMOVAL<br />
            SERVICE
          </h1>
          <p className="text-sm md:text-2xl text-white/90 mb-6 md:mb-10 max-w-2xl mx-auto">
            Same-day service available • Licensed & insured<br />
            Eco-friendly disposal
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a href="tel:+18633000390" className="bg-emerald-500 hover:bg-emerald-600 text-white px-5 md:px-8 py-2.5 md:py-4 rounded-full text-sm md:text-lg font-bold transition-colors flex items-center gap-2 whitespace-nowrap cursor-pointer">
              <i className="ri-phone-fill text-base md:text-xl"></i>
              CALL NOW: (863) 300-0390
            </a>
          </div>
          <div className="flex flex-row gap-8 md:gap-8 justify-center items-center mt-8 md:mt-16">
            <div className="text-center">
              <div className="text-xl md:text-4xl font-bold text-white">500+</div>
              <div className="text-xs md:text-sm text-white/80 mt-0.5">Jobs Completed</div>
            </div>
            <div className="text-center">
              <div className="text-xl md:text-4xl font-bold text-white flex items-center gap-1 md:gap-2 justify-center">
                4.9
                <i className="ri-star-fill text-yellow-400 text-lg md:text-3xl"></i>
              </div>
              <div className="text-xs md:text-sm text-white/80 mt-0.5">Customer Rating</div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">COMPREHENSIVE SERVICES</div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Everything You Need Gone 🚛</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">From single items to complete property cleanouts, we handle it all with professional care and eco-friendly disposal</p>
          </div>

          {/* Main Service Categories */}
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <div className="group relative h-80 rounded-2xl overflow-hidden cursor-pointer">
              <div className="absolute inset-0 w-full h-full">
                <img 
                  src="https://readdy.ai/api/search-image?query=residential%20home%20interior%20cluttered%20with%20old%20furniture%20boxes%20household%20items%20junk%20removal%20before%20photo%20living%20room%20garage%20basement%20clean%20simple%20background%20professional%20service%20industry&width=600&height=800&seq=service1&orientation=portrait" 
                  alt="Residential Junk Removal" 
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                <div className="w-12 h-12 flex items-center justify-center bg-emerald-500 rounded-lg mb-3">
                  <i className="ri-home-4-line text-2xl"></i>
                </div>
                <h3 className="text-2xl font-bold mb-2">Residential Services</h3>
                <p className="text-white/90 text-sm">Complete home cleanouts and junk removal</p>
              </div>
            </div>

            <div className="group relative h-80 rounded-2xl overflow-hidden cursor-pointer">
              <div className="absolute inset-0 w-full h-full">
                <img 
                  src="https://readdy.ai/api/search-image?query=commercial%20office%20space%20with%20old%20desks%20chairs%20equipment%20boxes%20business%20junk%20removal%20warehouse%20storage%20area%20industrial%20cleanup%20professional%20service%20clean%20simple%20background&width=600&height=800&seq=service2&orientation=portrait" 
                  alt="Commercial Cleanouts" 
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                <div className="w-12 h-12 flex items-center justify-center bg-emerald-500 rounded-lg mb-3">
                  <i className="ri-building-line text-2xl"></i>
                </div>
                <h3 className="text-2xl font-bold mb-2">Commercial & Property</h3>
                <p className="text-white/90 text-sm">Business and property management solutions</p>
              </div>
            </div>

            <div className="group relative h-80 rounded-2xl overflow-hidden cursor-pointer">
              <div className="absolute inset-0 w-full h-full">
                <img 
                  src="https://readdy.ai/api/search-image?query=outdoor%20yard%20with%20branches%20leaves%20debris%20tree%20removal%20equipment%20professional%20landscaping%20cleanup%20service%20green%20natural%20background&width=600&height=800&seq=service4&orientation=portrait" 
                  alt="Outdoor Services" 
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                <div className="w-12 h-12 flex items-center justify-center bg-emerald-500 rounded-lg mb-3">
                  <i className="ri-plant-line text-2xl"></i>
                </div>
                <h3 className="text-2xl font-bold mb-2">Outdoor & Specialty</h3>
                <p className="text-white/90 text-sm">Yard waste and outdoor debris removal</p>
              </div>
            </div>
          </div>

          {/* Detailed Services Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {/* Residential Services */}
            <div className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 flex items-center justify-center bg-emerald-100 rounded-lg">
                  <i className="ri-home-4-line text-xl text-emerald-600"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900">Residential Junk Removal</h3>
              </div>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-emerald-500 mt-0.5 flex-shrink-0"></i>
                  <span>Household junk removal</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-emerald-500 mt-0.5 flex-shrink-0"></i>
                  <span>Furniture removal (couches, beds, dressers)</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-emerald-500 mt-0.5 flex-shrink-0"></i>
                  <span>Appliance removal (refrigerators, washers, dryers)</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-emerald-500 mt-0.5 flex-shrink-0"></i>
                  <span>Mattress & box spring removal</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-emerald-500 mt-0.5 flex-shrink-0"></i>
                  <span>Garage, attic & basement cleanouts</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-emerald-500 mt-0.5 flex-shrink-0"></i>
                  <span>Hot tub & shed removal</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-emerald-500 mt-0.5 flex-shrink-0"></i>
                  <span>Hoarding cleanup & estate cleanouts</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-emerald-500 mt-0.5 flex-shrink-0"></i>
                  <span>Move-in / move-out cleanouts</span>
                </li>
              </ul>
            </div>

            {/* Commercial Services */}
            <div className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 flex items-center justify-center bg-blue-100 rounded-lg">
                  <i className="ri-building-line text-xl text-blue-600"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900">Commercial & Property</h3>
              </div>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-blue-500 mt-0.5 flex-shrink-0"></i>
                  <span>Office & retail space cleanouts</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-blue-500 mt-0.5 flex-shrink-0"></i>
                  <span>Warehouse cleanouts</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-blue-500 mt-0.5 flex-shrink-0"></i>
                  <span>Construction site cleanup</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-blue-500 mt-0.5 flex-shrink-0"></i>
                  <span>Renovation debris removal</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-blue-500 mt-0.5 flex-shrink-0"></i>
                  <span>Property management cleanouts</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-blue-500 mt-0.5 flex-shrink-0"></i>
                  <span>Eviction & foreclosure cleanouts</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-blue-500 mt-0.5 flex-shrink-0"></i>
                  <span>Storage unit cleanouts</span>
                </li>
              </ul>
            </div>

            {/* Outdoor Services */}
            <div className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 flex items-center justify-center bg-green-100 rounded-lg">
                  <i className="ri-plant-line text-xl text-green-600"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900">Outdoor & Specialty</h3>
              </div>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-green-500 mt-0.5 flex-shrink-0"></i>
                  <span>Yard waste removal (branches, leaves, brush)</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-green-500 mt-0.5 flex-shrink-0"></i>
                  <span>Tree debris removal</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-green-500 mt-0.5 flex-shrink-0"></i>
                  <span>Storm debris cleanup</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-green-500 mt-0.5 flex-shrink-0"></i>
                  <span>Fence removal</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-green-500 mt-0.5 flex-shrink-0"></i>
                  <span>Playset & deck removal</span>
                </li>
              </ul>
            </div>

            {/* Eco-Friendly Services */}
            <div className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 flex items-center justify-center bg-teal-100 rounded-lg">
                  <i className="ri-recycle-line text-xl text-teal-600"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900">Eco-Friendly & Recycling</h3>
              </div>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-teal-500 mt-0.5 flex-shrink-0"></i>
                  <span>Appliance recycling</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-teal-500 mt-0.5 flex-shrink-0"></i>
                  <span>Electronics (e-waste) recycling</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-teal-500 mt-0.5 flex-shrink-0"></i>
                  <span>Metal recycling</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-teal-500 mt-0.5 flex-shrink-0"></i>
                  <span>Cardboard & paper recycling</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-teal-500 mt-0.5 flex-shrink-0"></i>
                  <span>Donation drop-offs for usable items</span>
                </li>
              </ul>
            </div>

            {/* Specialty Services */}
            <div className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 flex items-center justify-center bg-pink-100 rounded-lg">
                  <i className="ri-star-line text-xl text-pink-600"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900">Specialty & Add-On Services</h3>
              </div>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-pink-500 mt-0.5 flex-shrink-0"></i>
                  <span>Same-day or emergency service</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-pink-500 mt-0.5 flex-shrink-0"></i>
                  <span>Labor-only moving help</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-pink-500 mt-0.5 flex-shrink-0"></i>
                  <span>Heavy item lifting</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-pink-500 mt-0.5 flex-shrink-0"></i>
                  <span>Curbside pickup</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-pink-500 mt-0.5 flex-shrink-0"></i>
                  <span>Dumpster overflow removal</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-pink-500 mt-0.5 flex-shrink-0"></i>
                  <span>On-demand recurring pickup service</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-pink-500 mt-0.5 flex-shrink-0"></i>
                  <span>Light sweeping after removal</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Why Choose Us */}
          <div className="mt-16">
            <div className="text-center mb-12">
              <div className="text-sm font-medium text-gray-500 mb-2">WHY CHOOSE US</div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">Professional Service <span className="italic font-serif">You Can Trust</span></h2>
            </div>

            <div className="grid md:grid-cols-4 gap-6">
              <div className="bg-emerald-50 rounded-2xl p-6 text-center">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-truck-line text-5xl text-emerald-600"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Fast Service</h3>
                <p className="text-sm text-gray-600">Same-day and next-day appointments available</p>
              </div>

              <div className="bg-pink-50 rounded-2xl p-6 text-center">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-time-line text-5xl text-pink-600"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">24/7 Availability</h3>
                <p className="text-sm text-gray-600">Call us anytime for emergency removals</p>
              </div>

              <div className="bg-blue-50 rounded-2xl p-6 text-center">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-recycle-line text-5xl text-blue-600"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Eco-Friendly</h3>
                <p className="text-sm text-gray-600">We recycle and donate whenever possible</p>
              </div>

              <div className="bg-orange-50 rounded-2xl p-6 text-center">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-money-dollar-circle-line text-5xl text-orange-600"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Fair Pricing</h3>
                <p className="text-sm text-gray-600">Transparent quotes with no hidden fees</p>
              </div>
            </div>
          </div>

          {/* CTA Button */}
          <div className="text-center mt-12">
            <a href="tel:+18633000390" className="inline-block bg-emerald-500 hover:bg-emerald-600 text-white px-8 py-4 rounded-full text-lg font-bold transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-phone-fill mr-2"></i>
              CALL NOW FOR FREE QUOTE
            </a>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">TRANSPARENT PRICING</div>
              <h2 className="text-5xl font-bold text-gray-900 mb-6">No Hidden Fees<br />Upfront Quotes</h2>
              <p className="text-lg text-gray-600 mb-8">Our pricing is based on the volume of junk you need removed. We provide free estimates and you only pay for the space your items take up in our truck. All labor, loading, and disposal fees are included.</p>
              
              <div className="grid grid-cols-2 gap-4 mb-8">
                <div className="border-l-4 border-emerald-500 pl-4">
                  <div className="text-2xl font-bold text-gray-900">$150-200</div>
                  <div className="text-sm text-gray-600">1/4 Truck Load</div>
                </div>
                <div className="border-l-4 border-emerald-500 pl-4">
                  <div className="text-2xl font-bold text-gray-900">$250-350</div>
                  <div className="text-sm text-gray-600">1/2 Truck Load</div>
                </div>
                <div className="border-l-4 border-emerald-500 pl-4">
                  <div className="text-2xl font-bold text-gray-900">$400-500</div>
                  <div className="text-sm text-gray-600">3/4 Truck Load</div>
                </div>
                <div className="border-l-4 border-emerald-500 pl-4">
                  <div className="text-2xl font-bold text-gray-900">$500-650</div>
                  <div className="text-sm text-gray-600">Full Truck Load</div>
                </div>
              </div>

              <a href="tel:+18633000390" className="inline-block bg-gray-900 hover:bg-gray-800 text-white px-8 py-4 rounded-full text-lg font-semibold transition-colors cursor-pointer whitespace-nowrap">
                CALL FOR QUOTE →
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-20 bg-gray-50">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">TESTIMONIALS</div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900">What Our Customers Say</h2>
          </div>

          <div className="bg-white rounded-2xl p-8 md:p-12 shadow-lg relative">
            <div className="absolute top-8 left-8 bg-gray-900 text-white px-4 py-2 rounded-full flex items-center gap-2">
              <i className="ri-star-fill text-yellow-400"></i>
              <span className="font-bold">{testimonials[currentTestimonial].rating}.0</span>
            </div>

            <div className="mt-8">
              <div className="text-6xl text-emerald-500 font-serif leading-none mb-4">"</div>
              <p className="text-xl text-gray-700 leading-relaxed mb-8 font-serif italic">
                {testimonials[currentTestimonial].text}
              </p>
            </div>

            <div className="flex items-center gap-4">
              <div className="w-20 h-20 rounded-full overflow-hidden flex-shrink-0">
                <img 
                  src={testimonials[currentTestimonial].avatar} 
                  alt={testimonials[currentTestimonial].name}
                  className="w-full h-full object-cover"
                />
              </div>
              <div>
                <div className="text-xl font-bold text-gray-900">{testimonials[currentTestimonial].name}</div>
                <div className="text-sm text-gray-600 flex items-center gap-1">
                  <i className="ri-checkbox-circle-fill text-emerald-500"></i>
                  Verified Customer
                </div>
                <div className="text-sm text-gray-500">{testimonials[currentTestimonial].location}</div>
              </div>
            </div>

            <div className="flex gap-3 justify-end mt-8">
              <button
                onClick={() => setCurrentTestimonial((prev) => (prev === 0 ? testimonials.length - 1 : prev - 1))}
                className="w-12 h-12 border-2 border-gray-300 rounded-lg flex items-center justify-center hover:border-gray-400 transition-colors cursor-pointer"
              >
                <i className="ri-arrow-left-line text-xl text-gray-600"></i>
              </button>
              <button
                onClick={() => setCurrentTestimonial((prev) => (prev === testimonials.length - 1 ? 0 : prev + 1))}
                className="w-12 h-12 bg-gray-900 rounded-lg flex items-center justify-center hover:bg-gray-800 transition-colors cursor-pointer"
              >
                <i className="ri-arrow-right-line text-xl text-white"></i>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-20 bg-gray-100">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-3">Frequently Asked Questions</h2>
            <p className="text-lg text-gray-600">Everything you need to know</p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white rounded-xl shadow-sm overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-gray-50 transition-colors cursor-pointer"
                >
                  <h4 className="text-lg font-bold text-gray-900 pr-4">{faq.question}</h4>
                  <div className={`w-8 h-8 flex items-center justify-center rounded-full bg-emerald-500 flex-shrink-0 transition-transform ${openFaq === index ? 'rotate-45' : ''}`}>
                    <i className="ri-add-line text-xl text-white"></i>
                  </div>
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-5">
                    <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-emerald-600">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-4 md:mb-6">Ready to Clear Your Space?</h2>
          <p className="text-base md:text-xl text-white/90 mb-6 md:mb-8">Call now for same-day service</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:+18633000390" className="bg-white hover:bg-gray-100 text-emerald-600 px-6 md:px-8 py-3 md:py-4 rounded-full text-base md:text-lg font-bold transition-colors flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer">
              <i className="ri-phone-fill text-lg md:text-xl"></i>
              CALL (863) 300-0390
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white text-gray-900 py-12 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <img src="https://static.readdy.ai/image/f76823ce8be4897f48af4b03ccc6bd18/ac857630e4931604e864e54d30baa87b.png" alt="Junk Removal Company" className="h-16 w-auto mb-4" />
              <p className="text-emerald-600 font-semibold">Your Trusted Junk Removal Partner</p>
            </div>

            <div>
              <h3 className="text-lg font-bold mb-4">SERVICES</h3>
              <ul className="space-y-2 text-gray-600">
                <li><a href="#services" className="hover:text-emerald-600 transition-colors cursor-pointer">Residential Removal</a></li>
                <li><a href="#services" className="hover:text-emerald-600 transition-colors cursor-pointer">Commercial Cleanouts</a></li>
                <li><a href="#services" className="hover:text-emerald-600 transition-colors cursor-pointer">Estate Cleanouts</a></li>
                <li><a href="#services" className="hover:text-emerald-600 transition-colors cursor-pointer">Appliance Removal</a></li>
                <li><a href="#services" className="hover:text-emerald-600 transition-colors cursor-pointer">Yard Waste</a></li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-bold mb-4">CONTACT</h3>
              <ul className="space-y-3 text-gray-600">
                <li className="flex items-center gap-2">
                  <i className="ri-phone-line"></i>
                  <a href="tel:+18633000390" className="hover:text-emerald-600 transition-colors cursor-pointer">(863) 300-0390</a>
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-mail-line"></i>
                  <a href="mailto:info@junkremoval.com" className="hover:text-emerald-600 transition-colors cursor-pointer">info@junkremoval.com</a>
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-time-line"></i>
                  <span>24/7 Available</span>
                </li>
              </ul>
              <div className="flex gap-3 mt-4">
                <a href="#" className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center hover:bg-emerald-500 hover:text-white transition-colors cursor-pointer">
                  <i className="ri-facebook-fill"></i>
                </a>
                <a href="#" className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center hover:bg-emerald-500 hover:text-white transition-colors cursor-pointer">
                  <i className="ri-instagram-fill"></i>
                </a>
                <a href="#" className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center hover:bg-emerald-500 hover:text-white transition-colors cursor-pointer">
                  <i className="ri-google-fill"></i>
                </a>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-bold mb-4">STAY UPDATED</h3>
              <p className="text-gray-600 text-sm mb-4">Get seasonal tips & special offers</p>
              <div className="flex gap-2">
                <input
                  type="email"
                  placeholder="Your email"
                  className="flex-1 px-4 py-2 bg-gray-50 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                />
                <button className="w-10 h-10 bg-emerald-500 text-white rounded-full flex items-center justify-center hover:bg-emerald-600 transition-colors cursor-pointer flex-shrink-0">
                  <i className="ri-arrow-right-line"></i>
                </button>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-200 pt-6 text-center text-sm text-gray-500">
            <p>&copy; 2025 Junk Removal Company. All rights reserved. | <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="hover:text-emerald-600 transition-colors">Website Builder</a></p>
          </div>
        </div>
      </footer>

      {/* Sticky Bottom CTA */}
      <div className="fixed bottom-0 left-0 right-0 bg-gray-900 text-white py-3 px-4 shadow-2xl z-40 md:hidden">
        <a href="tel:+18633000390" className="w-full bg-emerald-500 hover:bg-emerald-600 text-white py-2.5 rounded-full text-center text-sm font-bold flex items-center justify-center gap-2 transition-colors cursor-pointer">
          <i className="ri-phone-fill text-base"></i>
          CALL NOW
        </a>
      </div>
    </div>
  );
}
